import { EventBus } from '../EventBus';
import { Canonical } from '../hashState';
import { fromJsonNumber, jsonNumber } from '../snapshot';
import { TerrainGrid, CellState, Cell } from './TerrainGrid';

/**
 * Construction's complete state. See `ConstructionManager.save()`.
 *
 * `lastNotificationAt` is `null` rather than `-Infinity` for "never": a
 * snapshot has to survive `JSON.stringify`, which turns infinities into `null`
 * anyway and would otherwise make a reload change the notification throttle.
 */
export interface ConstructionSnapshot {
  pours: Array<{ cell: string; at: number }>;
  treesRemoved: number;
  spotsPainted: number;
  lastNotificationAt: number | null;
  adoptedTerrainVersion: number;
}

export enum ConstructionPhase {
  SURVEY = 'survey',
  CLEAR = 'clear',
  GRADE = 'grade',
  POUR = 'pour',
  CURE = 'cure',
  PAINT = 'paint',
  COMPLETE = 'complete',
}

// Per-cell phase system: each cell progresses independently
export class ConstructionManager {
  private grid: TerrainGrid;
  private events: EventBus;
  private readonly CURE_DURATION = 3000; // 3 sim-seconds for curing

  /**
   * Sim-time reader, in milliseconds.
   *
   * Curing used to be driven by `performance.now()`, which made concrete set
   * while the tab was in the background and made a cell's cure progress a
   * function of when you happened to look at it. Both are the ambient-time
   * violation of `docs/determinism.md` rule 2, and both are why a server and a
   * browser could not have agreed on the same lot.
   */
  private now: () => number;

  // Track the sim-time at which each cell was poured (for per-cell curing)
  private pourTimestamps: Map<string, number> = new Map();

  /** Terrain version the pour-time map was last reconciled against. */
  private adoptedTerrainVersion = -1;

  // Stats tracking
  private treesRemoved = 0;
  private spotsPainted = 0;
  private lastNotificationTime = -Infinity;

  constructor(grid: TerrainGrid, events: EventBus, now: () => number) {
    this.grid = grid;
    this.events = events;
    this.now = now;
  }

  // Get the dominant phase (most common state) for UI display
  getCurrentPhase(): ConstructionPhase {
    const counts = this.grid.countCellsByState();
    const total = this.grid.getTotalCells();

    // Check from most advanced to least
    if (counts[CellState.PAINTED] > total * 0.8) return ConstructionPhase.COMPLETE;
    if (counts[CellState.PAINTED] > 0) return ConstructionPhase.PAINT;
    if (counts[CellState.CURED] > 0) return ConstructionPhase.CURE;
    if (counts[CellState.POURED] > 0) return ConstructionPhase.POUR;
    if (counts[CellState.GRADED] > 0) return ConstructionPhase.GRADE;
    if (counts[CellState.CLEARED] > 0) return ConstructionPhase.CLEAR;
    if (counts[CellState.SURVEYED] > 0) return ConstructionPhase.SURVEY;
    return ConstructionPhase.SURVEY;
  }

  // Progress is based on how many cells are fully developed (painted)
  getPhaseProgress(): number {
    const counts = this.grid.countCellsByState();
    const total = this.grid.getTotalCells();
    return counts[CellState.PAINTED] / total;
  }

  // Click advances this specific cell to its next state
  handleCellClick(x: number, y: number): boolean {
    const cell = this.grid.getCell(x, y);
    if (!cell) return false;

    // Can't click cells that are curing or already painted
    if (cell.state === CellState.POURED || cell.state === CellState.PAINTED) {
      return false;
    }

    // Can't click cells that are cured (they're ready for painting)
    // Actually, we CAN click cured cells to paint them
    this.advanceCellState(x, y, cell);
    return true;
  }

  private advanceCellState(x: number, y: number, cell: Cell): void {
    const now = this.now();

    switch (cell.state) {
      case CellState.NATURAL:
        this.grid.setCellState(x, y, CellState.SURVEYED);
        this.maybeNotify('Area surveyed. Development potential: EXCELLENT');
        break;

      case CellState.SURVEYED:
        if (cell.vegetationType === 'tree') {
          this.treesRemoved++;
        }
        this.grid.setCellState(x, y, CellState.CLEARED);
        if (cell.vegetationType === 'tree') {
          this.maybeNotify(`Tree removed. Total: ${this.treesRemoved}`);
        }
        break;

      case CellState.CLEARED:
        this.grid.setCellState(x, y, CellState.GRADED);
        this.events.emit('GRADING_VIBRATION', { x, y, intensity: 0.5 });
        break;

      case CellState.GRADED:
        this.grid.setCellState(x, y, CellState.POURED);
        cell.concreteLevel = 1;
        cell.cureProgress = 0;
        // Track when this cell was poured for individual curing
        this.pourTimestamps.set(`${x},${y}`, now);
        this.events.emit('CONCRETE_POURED', { x, y });
        this.maybeNotify('Concrete poured. Curing in progress...');
        break;

      case CellState.CURED:
        this.grid.setCellState(x, y, CellState.PAINTED);
        this.spotsPainted++;
        if (this.spotsPainted % 10 === 0) {
          this.maybeNotify(`${this.spotsPainted} spots marked!`);
        }
        break;
    }
  }

  private maybeNotify(message: string): void {
    const now = this.now();
    // Throttle notifications to avoid spam
    if (now - this.lastNotificationTime > 500) {
      this.events.emit('NOTIFICATION_SHOW', { message });
      this.lastNotificationTime = now;
    }
  }

  update(_dt: number): void {
    const now = this.now();

    // Adopt poured cells we have no pour time for. A loaded save restores cell
    // states and cure progress but not this map, so without this a restored
    // lot's wet concrete would stay wet forever. Gated on the terrain version so
    // the common case — nothing changed — costs a comparison, not a grid scan.
    if (this.adoptedTerrainVersion !== this.grid.getTerrainVersion()) {
      this.adoptedTerrainVersion = this.grid.getTerrainVersion();
      for (const cell of this.grid.getAllCells()) {
        if (cell.state !== CellState.POURED) continue;
        const key = `${cell.x},${cell.y}`;
        if (this.pourTimestamps.has(key)) continue;
        this.pourTimestamps.set(key, now - cell.cureProgress * this.CURE_DURATION);
      }
    }

    // Update per-cell curing
    this.pourTimestamps.forEach((pourTime, key) => {
      const elapsed = now - pourTime;
      const [x, y] = key.split(',').map(Number);
      const cell = this.grid.getCell(x, y);

      if (cell && cell.state === CellState.POURED) {
        cell.cureProgress = Math.min(1, elapsed / this.CURE_DURATION);

        // Cure complete for this cell
        if (elapsed >= this.CURE_DURATION) {
          cell.state = CellState.CURED;
          this.pourTimestamps.delete(key);
        }
      } else {
        // Cell is no longer poured, clean up
        this.pourTimestamps.delete(key);
      }
    });

    // Check for completion
    const progress = this.getPhaseProgress();
    if (progress >= 0.8) {
      const counts = this.grid.countCellsByState();
      if (counts[CellState.POURED] === 0) { // All cured
        // Could emit completion event here
      }
    }
  }

  getTreesRemoved(): number {
    return this.treesRemoved;
  }

  getSpotsPainted(): number {
    return this.spotsPainted;
  }

  /**
   * Construction's complete state.
   *
   * The pour map is what makes this worth capturing: a cell knows it is wet from
   * its own `cureProgress`, but only this map knows *when* it was poured, and
   * `update()` computes cure from the difference. A snapshot without it hands
   * the loader concrete that has to guess its own age — which is what the
   * adoption path in `update()` does for a lossy save, and what a total
   * snapshot exists to make unnecessary.
   */
  save(): ConstructionSnapshot {
    return {
      // Pour order, not cell order: a snapshot preserves iteration order rather
      // than canonicalising it (`core/snapshot.ts`). Curing happens to be
      // independent per cell, so nothing here depends on it — but the next thing
      // that iterates this map might, and it should not have to find out.
      pours: Array.from(this.pourTimestamps.entries()).map(([cell, at]) => ({ cell, at })),
      treesRemoved: this.treesRemoved,
      spotsPainted: this.spotsPainted,
      lastNotificationAt: jsonNumber(this.lastNotificationTime),
      adoptedTerrainVersion: this.adoptedTerrainVersion,
    };
  }

  /** Restore from `save()`. */
  load(state: ConstructionSnapshot): void {
    this.pourTimestamps.clear();
    for (const pour of state.pours) this.pourTimestamps.set(pour.cell, pour.at);
    this.treesRemoved = state.treesRemoved;
    this.spotsPainted = state.spotsPainted;
    // Null means "nothing has been announced yet", which is `-Infinity` live.
    this.lastNotificationTime = fromJsonNumber(state.lastNotificationAt, -Infinity);
    this.adoptedTerrainVersion = state.adoptedTerrainVersion;
  }

  /**
   * Pour times sorted by cell rather than taken in Map order, since a restored
   * lot inserts them in a different sequence than one that was paved a cell at
   * a time.
   */
  private sortedPours(): Array<{ cell: string; at: number }> {
    return Array.from(this.pourTimestamps.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
      .map(([key, at]) => ({ cell: key, at }));
  }

  /**
   * Order-stable view of construction state, for hashing.
   */
  canonicalState(): Canonical {
    const pours = this.sortedPours();

    return {
      treesRemoved: this.treesRemoved,
      spotsPainted: this.spotsPainted,
      pours,
    };
  }
}
